// Admin UsersPage — paginated user list with role assignment.
import { useState } from "react";
import { useFetch, patchJson } from "../../hooks/useApi.js";
import { ROLES } from "@dm-lms/shared/types";
import type { PaginatedResult, Role, User } from "@dm-lms/shared/types";

export default function UsersPage() {
  const [page, setPage] = useState(1);
  const { data, isLoading, error, refetch } = useFetch<PaginatedResult<User>>(`/api/users?page=${page}&pageSize=20`);
  const [savingId, setSavingId] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);

  async function changeRole(userId: string, role: Role) {
    setSavingId(userId);
    setSaveError(null);
    const result = await patchJson<null>(`/api/users/${userId}/role`, { role });
    setSavingId(null);
    if (!result.ok) {
      setSaveError(result.error);
      return;
    }
    refetch();
  }

  return (
    <div className="dm-page">
      <h1 className="text-2xl font-semibold text-dm-navy mb-6">Users</h1>

      {isLoading && <p className="text-dm-slate text-sm">Loading users…</p>}
      {error && <p className="text-red-600 text-sm">{error}</p>}
      {saveError && <p className="text-red-600 text-sm mb-3">{saveError}</p>}

      {!isLoading && !error && data && (
        <>
          {data.items.length === 0 ? (
            <p className="text-dm-slate text-sm">No users found.</p>
          ) : (
            <div className="overflow-x-auto rounded-lg border border-gray-200 bg-white">
              <table className="min-w-full text-sm">
                <thead className="bg-gray-50 text-left text-dm-slate">
                  <tr>
                    <th className="px-4 py-3 font-medium">Code</th>
                    <th className="px-4 py-3 font-medium">Name</th>
                    <th className="px-4 py-3 font-medium">Email</th>
                    <th className="px-4 py-3 font-medium">Role</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {data.items.map((u) => (
                    <tr key={u.id}>
                      <td className="px-4 py-3 text-dm-navy font-medium">{u.code}</td>
                      <td className="px-4 py-3">{u.name}</td>
                      <td className="px-4 py-3">{u.email}</td>
                      <td className="px-4 py-3">
                        <select
                          className="border border-gray-300 rounded-md px-2 py-1 text-sm disabled:opacity-50"
                          value={u.role}
                          disabled={savingId === u.id}
                          onChange={(e) => changeRole(u.id, e.target.value as Role)}
                        >
                          {ROLES.map((role) => (
                            <option key={role} value={role}>{role}</option>
                          ))}
                        </select>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <div className="flex items-center justify-between mt-4">
            <button
              type="button"
              className="text-sm px-3 py-1.5 rounded-md border border-gray-300 disabled:opacity-40"
              disabled={page <= 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
            >
              Previous
            </button>
            <span className="text-xs text-dm-slate">Page {data.page} · {data.total} total</span>
            <button
              type="button"
              className="text-sm px-3 py-1.5 rounded-md border border-gray-300 disabled:opacity-40"
              disabled={!data.hasMore}
              onClick={() => setPage((p) => p + 1)}
            >
              Next
            </button>
          </div>
        </>
      )}
    </div>
  );
}
